import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import {
	Autocomplete,
	Box,
	Grid,
	TextField,
	Checkbox,
	FormControlLabel,
	Button,
	IconButton,
	CircularProgress,
	Typography,
	MenuItem,
} from '@mui/material';
import { Close, Save, Delete } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { SketchPicker } from 'react-color';

import {
	createAdventure,
	deleteAdventure,
	getAdventure,
	updateAdventure
} from '../../common/services/adventuresRequest';
import { getAllCharacters } from '../../common/services/characterRequests';

import { USERS } from './../../common/constants';
import {
	GAME_SYSTEM_OPTIONS,
	getAdventureGameSystemType,
	isAlienGameSystemType,
} from '../../common/gameSystems/registry';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		margin: theme.spacing(2),
	},
	field: {
		marginBottom: theme.spacing(2),
	},
	button: {
		marginRight: theme.spacing(2),
	},
	loader: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100%',
	},
	visualProperties: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		width: '100%',
	},
}));

const AdventureEditor = () => {
	const classes = useStyles();
	const { adventureId } = useParams();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const isNew = adventureId === 'new';
	const [activePicker, setActivePicker] = useState(null);
	const [playerCharacterMap, setPlayerCharacterMap] = useState({});
	const [selectedMasters, setSelectedMasters] = useState([]);
	const [characterSearch, setCharacterSearch] = useState('');
	const [filteredCharacters, setFilteredCharacters] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(1);
	const [nextToken, setNextToken] = useState();

	
	const handlePickerClick = (picker) => {
		setActivePicker(picker);
	};

	const handlePickerClose = () => {
		setActivePicker(null);
	};

	const { register, handleSubmit, setValue, watch } = useForm({
		defaultValues: {
			name: '',
			description: '',
			isPublic: false,
			owner: null,
			masters: [],
			players: [],
			type: '',
			cover: '',
			visualProperties: {
				backgroundColor: '#FFFFFF',
				fontSize: 14,
			},
			properties: {
				maxPlayers: 10,
				difficulty: 'medium',
			},
		},
	});

	const [isLoading, setIsLoading] = useState(false);
	const [adventure, setAdventure] = useState(null);

	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const selectedGameSystemType = watch('type');
	const mastersLabel = isAlienGameSystemType(selectedGameSystemType)
		? t('adventureEditor.fields.alienMother')
		: t('adventureEditor.fields.masters');

	const getUserEmail = (user) => user?.Attributes?.find((att) => att.Name === 'email')?.Value || '';
	const getUsersByIds = (userIds = []) =>
		userIds
			.map((userId) => USERS.find((user) => user.Username === userId))
			.filter(Boolean);

	const createRelationshipKey = (currentMap) => {
		let nextIndex = Object.keys(currentMap).length;
		let candidateKey = `newPlayer${nextIndex}`;

		while (Object.prototype.hasOwnProperty.call(currentMap, candidateKey)) {
			nextIndex += 1;
			candidateKey = `newPlayer${nextIndex}`;
		}

		return candidateKey;
	};

	useEffect(() => {
		const fetchCharacters = async () => {
			try {
				const response = await getAllCharacters({
					size: rowsPerPage,
					next: nextToken,
				});
				const { Items, NextToken } = JSON.parse(response.body).data.characters;
				console.log('characters are', Items)
				setFilteredCharacters((prevCharacters) => [...prevCharacters, ...(Items || [])]);
				setNextToken(NextToken || '');
			} catch (error) {
				console.log('Error getting characters', error);
			}
		};

		fetchCharacters();
	}, []);

	useEffect(() => {
		const fetchAdventure = async () => {
			setIsLoading(true);
			const fetchedAdventure = await getAdventure(adventureId);
			setAdventure(fetchedAdventure);
			setValue('name', fetchedAdventure.name);
			setValue('description', fetchedAdventure.description || '');
			setValue('isPublic', fetchedAdventure.isPublic);
			setValue('owner', fetchedAdventure.owner);
			setValue('masters', fetchedAdventure.masters || []);
			setSelectedMasters(getUsersByIds(fetchedAdventure.masters || []));
			setValue('players', fetchedAdventure.players || []);
			setValue('type', getAdventureGameSystemType(fetchedAdventure));
			setValue('cover', fetchedAdventure.cover);
			setValue('visualProperties', fetchedAdventure.visualProperties);
			setValue('properties', fetchedAdventure.properties);
			setIsLoading(false);
		};
		if (!isNew) {
			fetchAdventure();
		}
	}, [adventureId, isNew, setValue]);

	useEffect(() => {
		if (adventure) {
			console.log("adventure.playerCharacterMap", adventure.playerCharacterMap)
			setPlayerCharacterMap(adventure.playerCharacterMap || {});
		}
	}, [adventure]);

	useEffect(() => {
		console.log('playerCharacterMap', playerCharacterMap)
	}, [playerCharacterMap]);

	useEffect(() => {
		setFilteredCharacters(filterCharacters(characterSearch));
	}, [characterSearch]);

	const filterCharacters = (searchText) => {
		if (!searchText) {
			return filteredCharacters;
		}
		return filteredCharacters.filter((character) =>
			character.name.toLowerCase().includes(searchText.toLowerCase())
		);
	};

	const handleSave = async (data) => {
		console.log('data', data)
		setIsLoading(true);
		const editedAdventure = {
			...adventure,
			...data,
			type: data.type,
			gameSystemId: data.type,
			masters: Array.isArray(data.masters) ? data.masters.filter(Boolean) : [],
			players: Object.values(playerCharacterMap).map(({ playerId }) => playerId).filter(Boolean),
			playerCharacterMap,
			modified: Date.now(),
		};
		console.log('edited', editedAdventure)
		try {
			let savedAdventure = null;
			if (adventure?.ID) {
				savedAdventure = await updateAdventure(editedAdventure);
			} else {
				savedAdventure = await createAdventure(editedAdventure);
			}


			setIsLoading(false);
			if (savedAdventure) {
				setAdventure(savedAdventure);
				setValue('name', savedAdventure.name);
				setValue('description', savedAdventure.description || '');
				setValue('isPublic', savedAdventure.isPublic);
				setValue('owner', savedAdventure.owner);
				setValue('masters', savedAdventure.masters || []);
				setSelectedMasters(getUsersByIds(savedAdventure.masters || []));
				setValue('players', savedAdventure.players);
				setValue('type', getAdventureGameSystemType(savedAdventure));
				setValue('cover', savedAdventure.cover);
				setValue('visualProperties', savedAdventure.visualProperties);
				setValue('properties', savedAdventure.properties);
			}
		} catch (error) {
			setIsLoading(false);
			showError(error.message);
		}
	};

	const handleDelete = async () => {
		setIsLoading(true);
		try {
			await deleteAdventure(adventure.ID);
			navigate('/');
		} catch (error) {
			showError(error.message);
		}
		setIsLoading(false);
	};

	const showError = (message) => {
		enqueueSnackbar(message, {
			variant: 'error',
			anchorOrigin: {
				vertical: 'bottom',
				horizontal: 'center',
			},
			action: (key) => (
				<Button onClick={onClickDismiss(key)}>
					<Close color={'white'}></Close>
				</Button>
			),
			preventDuplicate: true,
		});
	};

	const onClickDismiss = (key) => () => {
		closeSnackbar(key);
	};

	if (isLoading) {
		return (
			<Box className={classes.loader}>
				<CircularProgress />
			</Box>
		);
	}
	return (
		<Box className={classes.root}>
			<form onSubmit={handleSubmit(handleSave)} className={classes.root}>
				<section>
					<Typography variant="h6" gutterBottom>
						{t('adventureEditor.sections.name')}
					</Typography>
					<TextField className={classes.field} {...register('name')} />
					<Typography variant="h6" gutterBottom>
						{t('adventureEditor.sections.description')}
					</Typography>
					<TextField className={classes.field} {...register('description')} multiline />
				</section>
				<section>
					<Typography variant="h6" gutterBottom>
						{t('adventureEditor.sections.visibility')}
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								{...register('isPublic')}
								color="primary"
								checked={watch('isPublic')}
							/>
						}
						label={t('adventureEditor.fields.public')}
					/>
				</section>

				<section>
					<Typography variant="h6" gutterBottom>
						{t('adventureEditor.sections.access')}
					</Typography>
					<Autocomplete
						multiple
						options={USERS}
						getOptionLabel={getUserEmail}
						value={selectedMasters}
						onChange={(_, newValue) => {
							setSelectedMasters(newValue);
							setValue(
								'masters',
								newValue.map((user) => user.Username),
								{ shouldDirty: true }
							);
						}}
						renderInput={(params) => (
							<TextField {...params} className={classes.field} label={mastersLabel} />
						)}
					/>
				</section>
				<section>
					<Typography variant="h6" gutterBottom>
						{t('adventureEditor.sections.details')}
					</Typography>
					<TextField
						className={classes.field}
						label={t('adventureEditor.fields.gameSystem')}
						select
						fullWidth
						value={watch('type') || ''}
						onChange={(event) =>
							setValue('type', event.target.value, {
								shouldDirty: true,
							})
						}
					>
						{GAME_SYSTEM_OPTIONS.map((option) => (
							<MenuItem key={option.id} value={option.id}>
								{option.name} ({option.id})
							</MenuItem>
						))}
					</TextField>
				</section>
				<section>
					<Typography variant="h6" gutterBottom>
						{t('adventureEditor.sections.cover')}
					</Typography>
					<TextField
						className={classes.field}
						{...register('cover')}
						label={t('adventureEditor.fields.cover')}
					/>
				</section>
				{/* <section>
					<Typography variant="h6" gutterBottom>
						Visual Properties
					</Typography>
					<div>
						{Object.entries(watch('visualProperties') || {}).map(
							([key, value], index) => (
								<div key={key}>
									<TextField
										className={classes.field}
										label="Key"
										value={key}
										onChange={(event) => {
											const newKey = event.target.value;
											const newVisualProperties = {
												...watch('visualProperties'),
											};
											const oldValue = newVisualProperties[key];
											delete newVisualProperties[key];
											newVisualProperties[newKey] = oldValue;
											setValue('visualProperties', newVisualProperties, {
												shouldDirty: true,
											});
										}}
									/>
									<TextField
										className={classes.field}
										label="Value"
										value={value}
										onChange={(event) => {
											const newValue = event.target.value;
											setValue(`visualProperties.${key}`, newValue);
										}}
									/>
									<IconButton
										aria-label="delete"
										onClick={() => {
											const { [key]: omit, ...rest } =
												watch('visualProperties');
											setValue('visualProperties', rest);
										}}>
										<Delete />
									</IconButton>
								</div>
							)
						)}
					</div>
					<Button
						variant="outlined"
						color="primary"
						onClick={() => {
							const newKey = `newKey${
								Object.keys(watch('visualProperties') || {}).length
							}`;
							setValue(`visualProperties.${newKey}`, '');
						}}>
						Add Property
					</Button>
				</section>

				<section>
					<Typography variant="h6" gutterBottom>
						Properties
					</Typography>
					<TextField
						className={classes.field}
						{...register('properties')}
						label="Properties"
					/>
				</section> */}

				{/* <section>
					<Typography variant="h6" gutterBottom>
						Visual Properties
					</Typography>

					<Box className={classes.visualProperties}>
						<TextField
							className={classes.field}
							{...register('visualProperties.fontSize')}
							label="Font size"
							type="number"
						/>
						<div>
							<Typography variant="subtitle1" gutterBottom>
								Background Color
							</Typography>
							<SketchPicker
								color={watch('visualProperties.backgroundColor') || '#fff'}
								onChange={(color) =>
									setValue('visualProperties.backgroundColor', color.hex)
								}
							/>
						</div>
						<TextField
							className={classes.field}
							{...register('visualProperties.backgroundImage')}
							label="Background image"
							type="text"
						/>
						<div>
							<Typography variant="subtitle1" gutterBottom>
								Border Color
							</Typography>
							<SketchPicker
								color={watch('visualProperties.border') || '#000'}
								onChange={(color) => setValue('visualProperties.border', color.hex)}
							/>
						</div>
					</Box>
				</section> */}
				<section>
					<Typography variant="h6" gutterBottom>
						{t('adventureEditor.sections.visualProperties')}
					</Typography>

					<Box className={classes.visualProperties}>
						<TextField
							className={classes.field}
							{...register('visualProperties.fontSize')}
							label={t('adventureEditor.fields.fontSize')}
							type="number"
						/>

						<div>
							<Typography variant="subtitle1" gutterBottom>
								{t('adventureEditor.fields.backgroundColor')}
							</Typography>
							{activePicker === 'visualProperties.backgroundColor' ? (
								<div>
									<div
										onClick={handlePickerClose}
										style={{
											position: 'fixed',
											top: 0,
											right: 0,
											bottom: 0,
											left: 0,
										}}
									/>
									<SketchPicker
										color={watch('visualProperties.backgroundColor') || '#fff'}
										onChange={(color) =>
											setValue('visualProperties.backgroundColor', color.hex)
										}
									/>
								</div>
							) : (
								<div
									onClick={() =>
										handlePickerClick('visualProperties.backgroundColor')
									}
									style={{
										width: '36px',
										height: '14px',
										borderRadius: '2px',
										background:
											watch('visualProperties.backgroundColor') || '#fff',
										cursor: 'pointer',
									}}
								/>
							)}
						</div>

						<TextField
							className={classes.field}
							{...register('visualProperties.backgroundImage')}
							label={t('adventureEditor.fields.backgroundImage')}
							type="text"
						/>

						<div>
							<Typography variant="subtitle1" gutterBottom>
								{t('adventureEditor.fields.borderColor')}
							</Typography>
							{activePicker === 'visualProperties.border' ? (
								<div>
									<div
										onClick={handlePickerClose}
										style={{
											position: 'fixed',
											top: 0,
											right: 0,
											bottom: 0,
											left: 0,
										}}
									/>
									<SketchPicker
										color={watch('visualProperties.border') || '#000'}
										onChange={(color) =>
											setValue('visualProperties.border', color.hex)
										}
									/>
								</div>
							) : (
								<div
									onClick={() => handlePickerClick('visualProperties.border')}
									style={{
										width: '36px',
										height: '14px',
										borderRadius: '2px',
										background: watch('visualProperties.border') || '#000',
										cursor: 'pointer',
									}}
								/>
							)}
						</div>
					</Box>
				</section>
				<section>
					<Typography variant="h6" gutterBottom>
						{t('adventureEditor.sections.playerCharacterRelationships')}
					</Typography>
					{Object.entries(playerCharacterMap).map(([player, { playerId, characterId }], index) => (
						<Box key={player} mb={2}>
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={6}>
									<Autocomplete
										options={USERS}
										getOptionLabel={(user) => user.Attributes.find((att) => att.Name === 'email')?.Value}
										value={USERS.find((user) => user.Username === playerId) || null}
										onChange={(_, newValue) => {
											const newPlayerId = newValue ? newValue.Username : null;
											setPlayerCharacterMap((prevMap) => ({
												...prevMap,
												[player]: {
													'playerId': newPlayerId,
													'characterId': characterId
												},
											}));
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label={t('adventureEditor.fields.player', {
													index: index + 1,
												})}
											/>
										)}
									/>
								</Grid>
								<Grid item xs={6}>
									<Autocomplete
										freeSolo
										options={filteredCharacters.map((character) => character.name)}
										value={
											characterId
												? filteredCharacters.find(
													(character) => character.ID === characterId
												)?.name || 'noname'
												: ''
										}
										onInputChange={(_, newValue) => {
											//setCharacterSearch(newValue);
										}}
										onChange={(_, newValue) => {
											const selectedCharacter = filteredCharacters.find(
												(character) => character.name === newValue
											);
											console.log('in character player', player)
											console.log('in character player', playerId)
											console.log('in character player', characterId)
											setPlayerCharacterMap((prevMap) => ({
												...prevMap,
												[player]: {
													'playerId': playerId,
													'characterId': selectedCharacter ? selectedCharacter.ID : null
												},
											}));
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label={t('adventureEditor.fields.characterSheet', {
													index: index + 1,
												})}
											/>
										)}
									/>
								</Grid>
							</Grid>

							{/* Remove Button */}
							<Button
								variant="outlined"
								color="secondary"
								disabled={!playerId || !characterId}
								onClick={() => {
									setPlayerCharacterMap((prevMap) => {
										const newMap = { ...prevMap };
										delete newMap[player];
										return newMap;
									});
								}}
								sx={{ marginTop: 2 }}
							>
								{t('adventureEditor.actions.removeSelection')}
							</Button>
						</Box>
					))}

					{/* Add Player-Character Button */}
					<Button
						variant="outlined"
						color="primary"
						onClick={() => {
							const newPlayerId = createRelationshipKey(playerCharacterMap);
							setPlayerCharacterMap((prevMap) => ({
								...prevMap,
								[newPlayerId]: {},
							}));
						}}
					>
						{t('adventureEditor.actions.addPlayerCharacterRelationship')}
					</Button>
				</section>

				<Box>
					<Button
						className={classes.button}
						variant="contained"
						color="primary"
						type="submit"
						startIcon={<Save />}>
						{t('adventureEditor.actions.save')}
					</Button>
					<Button
						className={classes.button}
						variant="contained"
						color="secondary"
						onClick={handleDelete}
						startIcon={<Delete />}>
						{t('adventureEditor.actions.delete')}
					</Button>
				</Box>
			</form>
		</Box>
	);
};

export default AdventureEditor;
